import audiencesCRUDActions from '@mm/state/actions/AudiencesCRUDActions';
import { ESCAPE_KEY_NAME } from '@mm/constants/keys-codes';
import TagsCRUDActions from '@mm/state/actions/TagsCRUDActions';
import WidgetModel, {
  WidgetConditionalAudience,
  WidgetConditionalFilter,
  WidgetConditionalTag,
} from '@mm/state/model/WidgetModel';
import { tagsOfKinds } from '@mm/state/selectors/tags';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import closeIcon from 'assets/icons/close.svg';
import classNames from 'classnames';
import { cloneDeep, isEqual, pick } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import Button from '../../../../../../common/buttons/DeprecatedButton/DeprecatedButton';
import { OnClickElsewhere } from '../../../../../../common/components/OnClickElsewhere/OnClickElsewhere';
import { useKeyDown } from '../../../../../../common/hooks/use-key-down';
import './ConditionalLogic.pcss';
import { ConditionalLogicAttributeDetail } from './ConditionalLogic/ConditionalLogicAttributeDetail';
import { ConditionalLogicAttributes } from './ConditionalLogic/ConditionalLogicAttributes';
import { ConditionalLogicAudiences } from './ConditionalLogic/ConditionalLogicAudiences';
import { ConditionalLogicRoot } from './ConditionalLogic/ConditionalLogicRoot';
import { ConditionalLogicTags } from './ConditionalLogic/ConditionalLogicTags';

type OuterProps = {
  widget: WidgetModel;
  onUpdateConditionalLogic: (widget: Partial<WidgetModel>) => Promise<any>;
  onClose: () => void;
  isStartBotsLayout?: boolean;
  className?: string;
};
type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

export type WidgetModelConditionalLogic = Pick<
  WidgetModel,
  'conditionalAudiences' | 'conditionalFilters' | 'conditionalTags'
>;
export type ConditionalLogicModeProps = OuterProps &
  InnerProps & {
    editWidget: WidgetModelConditionalLogic;
    setEditWidget: (widget: WidgetModelConditionalLogic) => void;
    changesPending: boolean;
    setEditMode: (mode: EditModes) => void;
    editFilter?: WidgetConditionalFilter;
    setEditFilter: (filter?: WidgetConditionalFilter) => void;
    conditionalTags: WidgetConditionalTag[];
    conditionalFilters: WidgetConditionalFilter[];
    conditionalAudiences: WidgetConditionalAudience[];
    loadingAudienceVariables: boolean;
  };
type EditModes =
  | 'root'
  | 'tags'
  | 'attributes'
  | 'attribute-detail'
  | 'audiences';

type Props = InnerProps & OuterProps;
const ConditionalLogic = (props: Props) => {
  const {
    className,
    onClose,
    widget,
    chatbotId,
    getAudienceVariables,
    variables,
    audiencesBusy,
    facebookPageId,
    getTags,
    isStartBotsLayout,
  } = props;

  useEffect(() => {
    if (facebookPageId) {
      getTags(facebookPageId);
    }
  }, [facebookPageId, getTags]);

  const [editMode, setEditMode] = useState<EditModes>('root');
  const [editFilter, setEditFilter] = useState<WidgetConditionalFilter>();

  const [loadedAudienceVariables, setLoadedAudienceVariables] = useState(
    variables.length > 0,
  );
  const loadingAudienceVariables = audiencesBusy || !loadedAudienceVariables;
  useEffect(() => {
    getAudienceVariables(chatbotId);
    setLoadedAudienceVariables(true);
  }, [getAudienceVariables, chatbotId]);

  const relavantFieldsOnly = (w: WidgetModel) =>
    pick(w, ['conditionalAudiences', 'conditionalFilters', 'conditionalTags']);
  const [editWidget, setEditWidget] = useState<WidgetModelConditionalLogic>(
    cloneDeep(relavantFieldsOnly(widget)),
  );

  useEffect(
    () => setEditWidget(cloneDeep(relavantFieldsOnly(widget))),
    [JSON.stringify(widget)],
  );

  const changesPending = !isEqual(relavantFieldsOnly(widget), editWidget);
  const conditionalAudiences = editWidget.conditionalAudiences || [];
  const conditionalFilters = editWidget.conditionalFilters || [];
  const conditionalTags = editWidget.conditionalTags || [];

  const modeProps: ConditionalLogicModeProps = {
    ...props,
    editWidget,
    setEditWidget,
    changesPending,
    setEditMode,
    editFilter,
    setEditFilter,
    conditionalTags,
    conditionalFilters,
    conditionalAudiences,
    loadingAudienceVariables,
  };

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ESCAPE_KEY_NAME') {
        onClose();
      }
    },
    [onClose],
  );

  useKeyDown(onKeyDown);

  const onDragStart = (e: React.DragEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const containerClassName = classNames(
    className,
    isStartBotsLayout
      ? 'widget-conditional-logic__startbots disable-sortable-hoc'
      : 'widget-conditional-logic disable-sortable-hoc',
  );

  return (
    <OnClickElsewhere handleClick={onClose}>
      <div className={containerClassName} onDragStart={onDragStart}>
        <Button
          text
          className="widget-conditional-logic__close-button"
          onClick={onClose}
        >
          <img alt="Close" src={closeIcon} />
        </Button>
        {(() => {
          switch (editMode) {
            case 'tags':
              return <ConditionalLogicTags {...modeProps} />;
            case 'audiences':
              return <ConditionalLogicAudiences {...modeProps} />;
            case 'attributes':
              return <ConditionalLogicAttributes {...modeProps} />;
            case 'attribute-detail':
              return <ConditionalLogicAttributeDetail {...modeProps} />;
            case 'root':
            default:
              return <ConditionalLogicRoot {...modeProps} />;
          }
        })()}
      </div>
    </OnClickElsewhere>
  );
};

const mapStateToProps = (state: StoreState) => ({
  tags: tagsOfKinds(state, ['regular']),
  audiences: state.audiences.entities,
  variables: state.audiences.variables,
  audiencesBusy: state.audiences.busy,
  chatbotId: activeChatbotIdSelector(state),
  facebookPageId: activeFacebookPageIdSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  getAudienceVariables: (chatbotId: number) =>
    dispatch(audiencesCRUDActions.getAudienceVariable(chatbotId)),
  getTags: (facebookPageId: number) =>
    dispatch(TagsCRUDActions.getAll(facebookPageId, true)),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(ConditionalLogic);
